import { InstituteProps } from "../types/institute.type";
import { firestore } from "../config/firebase.config";
import { fromFirestore, toFirestore } from "./firestoreAdapter";
import { collection, doc, addDoc, getDocs, updateDoc, deleteDoc, query, where, serverTimestamp, getDoc } from "firebase/firestore";
import { ImageProps } from "../types";
import { StorageReference } from "firebase/storage";
import { uploadResponsiveWebPImage } from "../utilities";
import Swal from "sweetalert2";

const collectionName = "institutes";
const collectionRef = collection(firestore, collectionName);

interface SubscriptionData{
    // endDate:Date
    level:number
    // startDate:Date
}

class IntitiuteService {

    async getAll(active: boolean ): Promise<InstituteProps[]> {
        const q = query(collectionRef, where("active", "==", active));
        const querySnapshot = await getDocs(q);

        const institutes: InstituteProps[] = querySnapshot.docs.map((doc) => {
            //return data compatible with data types specified in the tasks variable 
            return fromFirestore<InstituteProps>({
                id: doc.id,
                ...doc.data()
            } as InstituteProps);
        });
        // Return the first institute with the searched code
        return institutes;
    }

    async getById(id: string): Promise<InstituteProps | null> {
        const docRef = doc(collectionRef, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const docId = docSnap.id;
            return fromFirestore<InstituteProps>({
                id: docId,
                ...docSnap.data()
            } as InstituteProps);
        }
        return null;
    }

    async getByCode(code: string): Promise<InstituteProps[]> {
        const q = query(collectionRef, where("code", "==", code));
        const querySnapshot = await getDocs(q);

        const institutes: InstituteProps[] = querySnapshot.docs.map((doc) => {
            //return data compatible with data types specified in the tasks variable 
            return fromFirestore<InstituteProps>({
                id: doc.id,
                ...doc.data()
            } as InstituteProps);
        });
        // Return the first institute with the searched code
        return institutes;
    }

    async create(institute: InstituteProps): Promise<string> {
        // Add a new document with a generated id.
        const docData = await addDoc(collectionRef, {
            ...toFirestore(institute),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return docData.id;
    }

    async update(id: string, value: any): Promise<boolean> {
        try {
            const docRef = doc(firestore, collectionName, id);
            const docSnap = await getDoc(docRef);
            const newValue = { ...value };
            newValue.updatedAt = serverTimestamp();

            if (docSnap.exists()) {
                await updateDoc(docRef, newValue);
                return true;
            }

            // Fallback: search by code if not found directly by id
            const q = query(collectionRef, where("code", "==", id));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const targetDoc = querySnapshot.docs[0];
                await updateDoc(targetDoc.ref, newValue);
                return true;
            }

            console.error(`Error updating: institutes/${id} - Document not found`);
            return false;
        } catch (error) {
            console.error(`Error updating: institutes/${id}`);
            console.error(`updating value: ${value}`);
            console.error(error);
            return false;
        }
    }

    async delete(id: string): Promise<boolean> {

        const docRef = doc(firestore, collectionName, id);

        if (docRef.id == null) {
            console.error(`Error deleting: institutes/${id}`);
            console.error("Doesn't find Insitute ");
            return false;
        }

        try {
            await deleteDoc(docRef);
            return true;
        } catch (error) {
            console.error(`Error deleting: institutes/${id}`);
            console.error(error);
            return false;
        }
    }

    async setActive(id: string, active: boolean): Promise<boolean> {
        {
            const docRef = doc(firestore, collectionName, id);

            if (docRef == null) {
                console.error(`Error setting active: institutes/${id}`);
                console.error("Doesn't find Faculty");
                return false;
            }

            try {
                await updateDoc(docRef, {
                    active: active,
                    updatedAt: serverTimestamp()
                });// Add a new document with a generated id.
                return true;

            } catch (error) {
                console.error(`Error setting active: institutes/${id}`);
                console.error(`active value: ${active}`);
                console.error(error);
                return false;
            }
        }
    }

    async updateSubscriptioLevel(instituteId: string, data: SubscriptionData): Promise<boolean> {
        return this.updateSubscriptionLevel(instituteId, data.level);
    }

    async updateSubscriptionLevel(instituteId: string, level: number): Promise<boolean> {
        try {
            const numericLevel = Math.min(10, Math.max(0, Math.round(Number(level))));
            const updateData = {
                subscriptionLevel: numericLevel,
                updatedAt: serverTimestamp(),
            };

            const docRef = doc(firestore, collectionName, instituteId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                await updateDoc(docRef, updateData);
                return true;
            }

            // Fallback: search by code
            const q = query(collectionRef, where("code", "==", instituteId));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const targetDoc = querySnapshot.docs[0];
                await updateDoc(targetDoc.ref, updateData);
                return true;
            }

            console.error(`Error updating subscriptionLevel: Institute not found with id or code: ${instituteId}`);
            return false;
        } catch (error) {
            console.error(`Error updating subscriptionLevel for institute: ${instituteId}`, error);
            return false;
        }
    }

    async updateLogo(iesID: string, image: File): Promise<ImageProps | null> {
        try {
            const cleanName = image.name.replace(/\.[^/.]+$/, "");
            const basePath = `ies/${iesID}/logo/${cleanName}`;

            const uploadedImage = await uploadResponsiveWebPImage(basePath, image, "logo");
            Swal.fire({
                title: "Logo cargado exitosamente",
                icon: "success",
                timer: 2000,
            });

            const collectionRef = collection(firestore, "institutes");
            const docRef = doc(collectionRef, iesID);

            const updateData = {
                'logo': uploadedImage.url,
                'logoData': uploadedImage,
                'updatedAt': serverTimestamp()
            };
            await updateDoc(docRef, updateData);
            return uploadedImage;
        } catch (error) {
            console.error('Error uploading image:', error);
            Swal.fire({
                title: "Error al cargar logo",
                text: "Hubo un problema al subir la imagen",
                icon: "error"
            });
        }
        return null;
    }
    
    async optimizedImage(storageRef: StorageReference, file: File): Promise<ImageProps> {
        const path = storageRef.fullPath.replace(/\.[^/.]+$/, "");
        return uploadResponsiveWebPImage(path, file, "background");
    }
}

export default new IntitiuteService();
